import { ReactNode, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useParams, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Shell, SECTIONS } from "./components/Shell";
import Welcome from "./Welcome";
import Login from "./Login";
import Projects from "./studio/Projects";
import Dashboard from "./studio/Dashboard";
import ProjectData from "./studio/ProjectData";
import Brief from "./studio/Brief";
import Concepts from "./studio/Concepts";
import Presentation from "./studio/Presentation";
import Stages from "./studio/Stages";
import Schedule from "./studio/Schedule";
import Program from "./studio/Program";
import Reports from "./studio/Reports";
import Quotes from "./studio/Quotes";
import Billing from "./studio/Billing";
import Ledger from "./studio/Ledger";
import Records from "./studio/Records";
import Feedback from "./studio/Feedback";
import Team from "./studio/Team";
import Estimator from "./studio/Estimator";
import Rates from "./studio/Rates";
import Review from "./studio/Review";
import Drawings from "./studio/Drawings";
import Tendering from "./studio/Tendering";
import Rfis from "./studio/Rfis";
import Site from "./studio/Site";
import Snags from "./studio/Snags";
import CostPlan from "./studio/CostPlan";
import CostBrief from "./studio/CostBrief";
import Pipeline from "./studio/Pipeline";
import StudioTools from "./studio/StudioTools";
import Audit from "./studio/Audit";
import Moodboard from "./studio/Moodboard";
import Engagement from "./studio/Engagement";
import Portal from "./portal/Portal";
import ProjectPackagePage from "./portal/ProjectPackagePage";
import PartnerPage from "./portal/PartnerPage";
import TenderPage from "./portal/TenderPage";
import Integrations from "./Integrations";
import { useSession, getSession, bootstrapAuth, useAuthReady, can } from "./lib/auth";
import { getSyncStatus } from "./lib/sync";
import { actions, useStore } from "./lib/store";
import { SECTION_CAP } from "./lib/permissions";
import { readInboundProject, listenForStudioLink } from "./lib/bridge";
import { useUpdateReady, applyUpdate } from "./lib/update";
import { Icon } from "./components/Icons";

const SECTION_MAP: Record<string, () => JSX.Element | null> = {
  dashboard: Dashboard, data: ProjectData, brief: Brief, concept: Concepts, presentation: Presentation,
  stages: Stages, schedule: Schedule, program: Program, reports: Reports, quotes: Quotes, billing: Billing, ledger: Ledger, records: Records,
  feedback: Feedback, team: Team, estimator: Estimator, rates: Rates, review: Review, drawings: Drawings, tendering: Tendering, rfis: Rfis, site: Site, snags: Snags, costplan: CostPlan, costbrief: CostBrief, audit: Audit, moodboard: Moodboard, engagement: Engagement,
};

/** Cockpit is for studio + team sessions. Clients are bounced to their portal. */
function RequireStudio({ children }: { children: ReactNode }) {
  const s = useSession();
  if (!s) return <Navigate to="/login" replace />;
  if (s.role === "client") return <Navigate to={`/portal/${s.projectId}`} replace />;
  return <>{children}</>;
}

/** Public tokened drawing package — the token IS the auth, no sign-in. */
function PackageRoute() {
  const { token = "" } = useParams();
  return <ProjectPackagePage token={token} />;
}

/** Portal is for the matching client, or any studio/team member previewing it. */
function RequirePortal() {
  const s = useSession();
  const { pid } = useParams();
  if (!s) return <Navigate to="/login" replace />;
  if (s.role === "client" && s.projectId !== pid) return <Navigate to={`/portal/${s.projectId}`} replace />;
  return <Portal />;
}

/** A section the role can't reach. Said plainly rather than bounced silently —
 *  otherwise a link from a colleague just dumps you on the dashboard. */
function NoAccess({ section }: { section: string }) {
  const nav = useNavigate();
  const label = SECTIONS.find((s) => s.key === section)?.label ?? "That section";
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "60vh", padding: 24 }}>
      <div className="card pad" style={{ maxWidth: 420, textAlign: "center" }}>
        <div style={{ width: 42, height: 42, borderRadius: 42, background: "var(--paper-2)", display: "grid", placeItems: "center", margin: "0 auto 14px" }}>
          <Icon.gear width={18} style={{ color: "var(--ink-3)" }} />
        </div>
        <h3 className="serif" style={{ fontSize: 21 }}>{label} isn't open to you</h3>
        <p className="muted" style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.6 }}>
          Your role doesn't include this section. If you need it, ask the studio admin to switch it on in Team &amp; access.
        </p>
        <button className="btn ghost sm" style={{ marginTop: 16 }} onClick={() => nav(-1)}><Icon.back width={14} />Go back</button>
      </div>
    </div>
  );
}

/** Gate the section on its capability, not just on being signed in — the
 *  permission table has to hold at the door, not only on the buttons inside. */
function StudioSection() {
  const { section = "dashboard" } = useParams();
  const s = useSession();
  const { roleMatrix } = useStore();
  const Cmp = SECTION_MAP[section] ?? Dashboard;
  const cap = SECTION_CAP[section];
  const allowed = !cap || can(s, cap, roleMatrix);
  return (
    <RequireStudio>
      <Shell>{allowed ? <Cmp /> : <NoAccess section={section} />}</Shell>
    </RequireStudio>
  );
}

/** The Project Studio sends the job it created back as ?linked=… when it can't
 *  reach us by postMessage (opened in-place, or the popup was blocked). Record
 *  it and strip the params so the URL stays clean. */
const handledLinks = new Set<string>();

function StudioLinkCatcher() {
  const loc = useLocation();
  const nav = useNavigate();
  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const target = q.get("linked");
    const id = q.get("linkId");
    if ((target !== "pm" && target !== "bid") || !id) return;
    const pid = loc.pathname.split("/")[2];
    if (!pid) return;

    // Do this once per link — the effect re-runs under StrictMode and on any
    // re-render, and the work below outlives the effect deliberately.
    const key = `${pid}:${target}:${id}`;
    if (handledLinks.has(key)) return;
    handledLinks.add(key);

    const ref = {
      id, code: q.get("linkCode") || "", url: q.get("linkUrl") || "",
      sheets: Number(q.get("linkSheets")) || 0, at: new Date().toISOString(),
    };
    ["linked", "linkId", "linkCode", "linkUrl", "linkSheets"].forEach((k) => q.delete(k));
    nav({ pathname: loc.pathname, search: q.toString() }, { replace: true });

    /* We land here on a full page load — exactly when the workspace hydrates
       from D1, and that replaces local state wholesale. Write the link only
       once sync has settled, or it's silently overwritten. Not tied to effect
       cleanup on purpose: a StrictMode unmount must not cancel it. */
    void (async () => {
      const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
      await sleep(300);                                   // let bootstrapAuth start sync
      const t0 = Date.now();
      while (getSyncStatus() === "syncing" && Date.now() - t0 < 8000) await sleep(120);
      actions.setStudioLink(pid, target, ref);
    })();
  }, [loc.search, loc.pathname, nav]);
  return null;
}

/** /import?data=… — a project handed over from the Contract Generator. */
function ImportBridge() {
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return; done.current = true;
    const inbound = readInboundProject(window.location.search);
    if (!inbound) { nav("/", { replace: true }); return; }
    const id = actions.createProject(inbound);
    const s = getSession();
    nav(s && s.role !== "client" ? `/studio/${id}/dashboard` : "/login?role=studio", { replace: true });
  }, [nav]);
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--paper)" }}>
      <div className="row" style={{ gap: 12, color: "var(--ink-2)" }}>
        <Icon.report width={22} style={{ color: "var(--accent-ink)" }} />
        <span className="serif" style={{ fontSize: 22 }}>Importing project from Contract Generator…</span>
      </div>
    </div>
  );
}

/** A change is deployed but this tab is still on the old bundle — one tap to
 *  reload onto it. Without this, a shipped fix (e.g. a permission gate) doesn't
 *  reach an open session until a manual hard-refresh. */
function UpdatePill() {
  const ready = useUpdateReady();
  if (!ready) return null;
  return (
    <button onClick={() => void applyUpdate()} className="row"
      style={{ position: "fixed", bottom: "calc(20px + env(safe-area-inset-bottom))", right: 18, zIndex: 200, gap: 8,
        padding: "10px 16px", borderRadius: 40, border: "1px solid var(--line)", background: "var(--ink)", color: "var(--paper)",
        cursor: "pointer", boxShadow: "var(--shadow-lg)", fontSize: 13, fontWeight: 500, animation: "floatIn .25s ease" }}>
      <Icon.spark width={14} style={{ color: "var(--accent)" }} />New version — refresh
    </button>
  );
}

export default function App() {
  const ready = useAuthReady();
  useEffect(() => { bootstrapAuth(); }, []);
  // The Project Studio posts back the job it created from a handoff.
  useEffect(() => listenForStudioLink((pid, target, ref) => actions.setStudioLink(pid, target, ref)), []);
  if (!ready) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--paper)" }}>
      <div className="serif" style={{ fontSize: 22, color: "var(--ink-3)" }}>HD Studio</div>
    </div>
  );
  return (
    <BrowserRouter>
      <UpdatePill />
      <StudioLinkCatcher />
      <Routes>
        <Route path="/" element={<Welcome />} />
        <Route path="/pkg/:token" element={<PackageRoute />} />
        <Route path="/partner/:token" element={<PartnerPage />} />
        <Route path="/tender/:token" element={<TenderPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/import" element={<ImportBridge />} />
        <Route path="/studio" element={<Navigate to="/studio/projects" replace />} />
        <Route path="/studio/projects" element={<RequireStudio><Projects /></RequireStudio>} />
        <Route path="/studio/pipeline" element={<RequireStudio><Pipeline /></RequireStudio>} />
        <Route path="/studio/tools" element={<RequireStudio><StudioTools /></RequireStudio>} />
        <Route path="/studio/:pid/:section" element={<StudioSection />} />
        <Route path="/portal/:pid" element={<RequirePortal />} />
        <Route path="/portal/:pid/:section" element={<RequirePortal />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
